import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useSelectedFile } from './useSelectedFile';
import { clearSearchProperties, searchProperties, searchPropertiesNext, selectProperty } from 'store/slices/properties';

export const useSearchProperties = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [showResults, setShowResults] = useState(false);

  const { selectFileData } = useSelectedFile();

  const {
    value: searchResults,
    loading: searchLoading,
    offset,
    hasMore,
  } = useSelector((state) => state.properties.searchProperties);

  useEffect(() => {
    if (searchValue.trim() === '') {
      setShowResults(false);
      return;
    }

    const timer = setTimeout(() => {
      const normalized = searchValue.replace(/\s+/g, ' ').trim();

      dispatch(searchProperties({ query: normalized }))
        .unwrap()
        .then(() => {
          setShowResults(true);
        })
        .catch(() => {
          setShowResults(true);
          dispatch(clearSearchProperties());
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue, dispatch]);

  const fetchNext = useCallback(() => {
    const normalized = searchValue.replace(/\s+/g, ' ').trim();

    dispatch(
      searchPropertiesNext({
        query: normalized,
        offset,
      })
    );
  }, [dispatch, searchValue, offset]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleFocus = () => {
    if (searchValue) {
      setShowResults(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  const clearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const handleSelectResult = (item) => {
    selectFileData([item]);
    dispatch(selectProperty(item));
    navigate(`/properties/${item.id}`);
    setShowResults(false);
  };

  return {
    searchValue,
    searchResults,
    searchLoading,
    showResults,
    setShowResults,
    handleSearchChange,
    handleFocus,
    handleBlur,
    handleSelectResult,
    clearSearch,
    fetchNext,
    hasMore,
    offset,
  };
};
